import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Clock, BellRing, UserX, Coffee, UtensilsCrossed, Droplet, Moon, Filter, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-UI zur Konfiguration der automatischen Push-Reminder:
 *   - appointment_reminder_60min (Termin in 60 Min)
 *   - appointment_reminder_24h   (Termin morgen)
 *   - no_booking_reminder        (Seit X Tagen kein Termin)
 *   - inactivity_reminder        (App X Tage nicht geöffnet)
 *
 * Jeder Typ: EIN/AUS-Toggle + anpassbare Zeit-Schwelle.
 * Persistenz via PUT /api/admin/mobile-app/push-notifications/{type}.
 */
const REMINDER_SPECS = [
  {
    type: 'appointment_reminder_60min',
    icon: Clock,
    label: '60-Min-Vor-Termin',
    description: 'Push 60 Minuten vor einem Termin ("⏰ In 60 Minuten: EMS Training um 10 Uhr").',
    tunable: { field: 'minutes_before', label: 'Minuten vorher', min: 10, max: 240, step: 5, unit: 'Min' },
  },
  {
    type: 'appointment_reminder_24h',
    icon: BellRing,
    label: '24-Stunden-Vor-Termin',
    description: 'Push am Vortag ("📅 Termin morgen: Personal Training um 9 Uhr").',
    tunable: { field: 'hours_before', label: 'Stunden vorher', min: 4, max: 72, step: 1, unit: 'h' },
  },
  {
    type: 'no_booking_reminder',
    icon: UserX,
    label: 'Kein-Termin-Reminder',
    description: 'Push an Kunden ohne Buchung seit X Tagen ("Hey {name}! 💪 Schon länger kein Training?").',
    tunable: { field: 'no_booking_days_threshold', label: 'Tage ohne Buchung', min: 3, max: 90, step: 1, unit: 'Tage' },
    extra: [
      { field: 'repeat_after_days', label: 'Wiederholen nach', min: 3, max: 30, step: 1, unit: 'Tagen' },
      { field: 'max_reminders', label: 'Max. Erinnerungen', min: 1, max: 10, step: 1, unit: 'x' },
    ],
  },
  {
    type: 'inactivity_reminder',
    icon: UserX,
    label: 'App-Inaktivitäts-Reminder',
    description: 'Push an Kunden die App X Tage nicht geöffnet haben.',
    tunable: { field: 'inactivity_days_threshold', label: 'Tage inaktiv', min: 3, max: 90, step: 1, unit: 'Tage' },
    extra: [
      { field: 'repeat_after_days', label: 'Wiederholen nach', min: 3, max: 30, step: 1, unit: 'Tagen' },
      { field: 'max_reminders', label: 'Max. Erinnerungen', min: 1, max: 10, step: 1, unit: 'x' },
    ],
  },
  {
    type: 'meal_reminder_breakfast',
    icon: Coffee,
    label: 'Frühstücks-Erinnerung',
    description: 'Tägliche Push-Erinnerung, das Frühstück zu tracken (respektiert den Mahlzeiten-Schalter des Kunden).',
    timeTunable: { field: 'default_time', label: 'Uhrzeit' },
  },
  {
    type: 'meal_reminder_lunch',
    icon: UtensilsCrossed,
    label: 'Mittags-Erinnerung',
    description: 'Tägliche Push-Erinnerung, das Mittagessen zu tracken.',
    timeTunable: { field: 'default_time', label: 'Uhrzeit' },
  },
  {
    type: 'meal_reminder_dinner',
    icon: UtensilsCrossed,
    label: 'Abendessen-Erinnerung',
    description: 'Tägliche Push-Erinnerung, das Abendessen zu tracken.',
    timeTunable: { field: 'default_time', label: 'Uhrzeit' },
  },
  {
    type: 'water_reminder',
    icon: Droplet,
    label: 'Wasser-Erinnerung',
    description: 'Erinnerung ans Trinken im Intervall, tagsüber (nur Kunden, die Wasser-Reminder aktiviert haben; zeigt echten Tagesfortschritt).',
    tunable: { field: 'interval_minutes', label: 'Intervall', min: 30, max: 480, step: 30, unit: 'Min' },
  },
  {
    type: 'nutrition_reminder_gate',
    icon: Filter,
    label: 'Ernährungs-Reminder: nur aktive Tracker',
    description: 'Wenn AN, gehen Mahlzeiten- & Wasser-Reminder NUR an Kunden, die im Aktiv-Fenster tatsächlich etwas eingetragen haben. Kunden, die noch nie (oder länger nicht) getrackt haben, bekommen KEINE Erinnerungen mehr — auch neue Kunden erhalten keinen „Anschub". AUS = alle bekommen sie (wie früher).',
    tunable: { field: 'tracking_window_days', label: 'Aktiv-Fenster', min: 1, max: 90, step: 1, unit: 'Tage' },
  },
  {
    type: 'nutrition_reactivation',
    icon: Sparkles,
    label: 'Reaktivierung: „Lust, wieder zu tracken?"',
    description: 'Sanfter Push an Kunden, die früher getrackt haben, aber seit einer Weile pausieren. Begrenzt & mit Cooldown, damit es nicht nervt.',
    tunable: { field: 'reactivation_days_threshold', label: 'Pausiert seit', min: 7, max: 180, step: 1, unit: 'Tage' },
    extra: [
      { field: 'repeat_after_days', label: 'Cooldown', min: 7, max: 180, step: 1, unit: 'Tagen' },
      { field: 'max_reminders', label: 'Max. Erinnerungen', min: 1, max: 5, step: 1, unit: 'x' },
    ],
  },
];

const PushReminderConfig = () => {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [savingGlobal, setSavingGlobal] = useState(false);

  const token = () => localStorage.getItem('token');

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/mobile-app/push-notifications`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const map = {};
      for (const n of res.data?.notifications || []) {
        map[n.notification_type] = n;
      }
      setConfigs(map);
      setGlobalSettings(res.data?.global_settings || null);
    } catch (e) {
      toast.error('Fehler beim Laden der Push-Configs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = async (type, patch) => {
    const current = configs[type];
    if (!current) return;
    const next = { ...current, ...patch };
    setConfigs((prev) => ({ ...prev, [type]: next }));
    setSaving(type);
    try {
      await axios.put(
        `${API}/admin/mobile-app/push-notifications/${type}`,
        next,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success('Gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
      // Rollback
      setConfigs((prev) => ({ ...prev, [type]: current }));
    } finally {
      setSaving(null);
    }
  };

  const saveGlobal = async (patch) => {
    const next = { ...(globalSettings || {}), ...patch };
    setGlobalSettings(next);
    setSavingGlobal(true);
    try {
      await axios.put(
        `${API}/admin/mobile-app/push-notifications/global-settings`,
        next,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success('Gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingGlobal(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500 p-4">Lädt…</p>;
  }

  return (
    <div className="space-y-3" data-testid="push-reminder-config">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <BellRing className="w-5 h-5 text-amber-500" />
            Push-Reminder-Konfiguration
          </h3>
          <p className="text-sm text-gray-500">
            Aktiviere/deaktiviere automatische Push-Reminder und passe Zeit-Schwellen an.
          </p>
        </div>
      </div>

      {globalSettings && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm" data-testid="global-quiet-hours">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={`p-2 rounded-lg ${globalSettings.quiet_hours_enabled ? 'bg-indigo-100' : 'bg-gray-200'}`}>
                <Moon className={`w-5 h-5 ${globalSettings.quiet_hours_enabled ? 'text-indigo-600' : 'text-gray-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-900">Ruhezeiten (Studio-Standard)</p>
                  {savingGlobal && <span className="text-xs text-amber-600">wird gespeichert…</span>}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Nachts keine (Marketing-)Pushes. Gilt für alle Kunden OHNE eigene Ruhezeiten-Einstellung in der App.
                </p>
              </div>
            </div>
            <Switch
              checked={!!globalSettings.quiet_hours_enabled}
              onCheckedChange={(v) => saveGlobal({ quiet_hours_enabled: v })}
              data-testid="toggle-global-quiet"
            />
          </div>
          {globalSettings.quiet_hours_enabled && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <TimeField
                label="Beginn"
                value={globalSettings.quiet_hours_start || '22:00'}
                testId="input-global-quiet-start"
                onBlur={(val) => saveGlobal({ quiet_hours_start: val })}
              />
              <TimeField
                label="Ende"
                value={globalSettings.quiet_hours_end || '07:00'}
                testId="input-global-quiet-end"
                onBlur={(val) => saveGlobal({ quiet_hours_end: val })}
              />
            </div>
          )}
        </div>
      )}

      {REMINDER_SPECS.map((spec) => {
        const cfg = configs[spec.type];
        if (!cfg) return null;
        const Icon = spec.icon;
        return (
          <div
            key={spec.type}
            className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            data-testid={`reminder-card-${spec.type}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div
                  className={`p-2 rounded-lg ${
                    cfg.enabled ? 'bg-amber-100' : 'bg-gray-200'
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${
                      cfg.enabled ? 'text-amber-600' : 'text-gray-400'
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">{spec.label}</p>
                    {saving === spec.type && (
                      <span className="text-xs text-amber-600">wird gespeichert…</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{spec.description}</p>
                </div>
              </div>
              <Switch
                checked={!!cfg.enabled}
                onCheckedChange={(v) => update(spec.type, { enabled: v })}
                data-testid={`toggle-${spec.type}`}
              />
            </div>

            {cfg.enabled && (spec.tunable || spec.timeTunable) && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {spec.timeTunable && (
                  <TimeField
                    label={spec.timeTunable.label}
                    value={cfg[spec.timeTunable.field]}
                    testId={`input-${spec.type}-${spec.timeTunable.field}`}
                    onBlur={(val) => update(spec.type, { [spec.timeTunable.field]: val })}
                  />
                )}
                {spec.tunable && (
                  <NumberField
                    label={spec.tunable.label}
                    value={cfg[spec.tunable.field]}
                    unit={spec.tunable.unit}
                    min={spec.tunable.min}
                    max={spec.tunable.max}
                    step={spec.tunable.step}
                    testId={`input-${spec.type}-${spec.tunable.field}`}
                    onBlur={(val) => update(spec.type, { [spec.tunable.field]: val })}
                  />
                )}
                {(spec.extra || []).map((ex) => (
                  <NumberField
                    key={ex.field}
                    label={ex.label}
                    value={cfg[ex.field]}
                    unit={ex.unit}
                    min={ex.min}
                    max={ex.max}
                    step={ex.step}
                    testId={`input-${spec.type}-${ex.field}`}
                    onBlur={(val) => update(spec.type, { [ex.field]: val })}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

function NumberField({ label, value, unit, min, max, step, onBlur, testId }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => setLocal(value ?? ''), [value]);
  return (
    <div className="space-y-1">
      <Label className="text-xs text-gray-500">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={min}
          max={max}
          step={step}
          value={local}
          data-testid={testId}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => {
            const n = parseInt(local, 10);
            if (!Number.isFinite(n)) return;
            const clamped = Math.max(min, Math.min(max, n));
            if (clamped !== value) onBlur(clamped);
          }}
          className="h-9 bg-white border-gray-300 text-gray-900"
        />
        <span className="text-xs text-gray-500">{unit}</span>
      </div>
    </div>
  );
}

function TimeField({ label, value, onBlur, testId }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => setLocal(value ?? ''), [value]);
  return (
    <div className="space-y-1">
      <Label className="text-xs text-gray-500">{label}</Label>
      <Input
        type="time"
        value={local}
        data-testid={testId}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local && local !== value) onBlur(local);
        }}
        className="h-9 bg-white border-gray-300 text-gray-900"
      />
    </div>
  );
}

export default PushReminderConfig;
